/**
 * Auth event listeners for desktop app
 *
 * Subscribes to auth package events and handles them appropriately:
 * - AUTH_ERROR (legacy SDK) → bridge to AUTH_LOGOUT
 * - HTTP errors → show toast notifications
 * - Logout → clean up app state and navigate to login
 * - Token refresh → update SDK if needed
 */

import { AuthEventNames } from '@littlebird/auth-client';
import { EventNames, globalEventBus } from '@littlebird/event-bus';
import posthog from 'posthog-js';
import { toast } from 'sonner';

import { agentsStore } from '@/features/agents/agents.store';
import { electronApis } from '@/features/electron-api';
import { resetMeetingCircles } from '@/features/meetings/meeting-circles-cache';
import { clearWebOnboardingSessionCompletion } from '@/features/onboarding-web/web-onboarding.flags';
import { clearWebOnboardingCoachMarkStorage } from '@/features/onboarding-web/web-onboarding.handoff';
import { routinesStore } from '@/features/routines/routines.state';
import { resetRoutineReadState } from '@/features/routines/use-routine-refresh';
import { logger } from '@/lib/logger';
import { littlebirdSdk } from '@/lib/sdk-init';
import { cleanupQueries } from '@/query/client';
import { userStore } from '@/stores/user.store';
import { inElectron } from '@/utils/capabilities';
import { clearLocalStorage } from '@/utils/localStorage';
import { isInAuthGatedRoute, navigate } from '@/utils/navigation-service';
import { clearSingularUser } from '@/utils/singular';

let isSetup = false;

/**
 * Setup auth event listeners
 * Call this once at app startup
 */
export function setupAuthListeners() {
  if (isSetup) return;
  isSetup = true;

  let logoutInProgress = false;

  globalEventBus.on(EventNames.AUTH_ERROR, (event) => {
    if (logoutInProgress) {
      logger.warn('AUTH_ERROR received during logout, ignoring', { event });
      return;
    }

    logger.error('Legacy AUTH_ERROR received, bridging to AUTH_LOGOUT', {
      event,
    });

    globalEventBus.emit(EventNames.AUTH_LOGOUT, {
      data: { reason: 'error' },
    });
  });

  globalEventBus.on(AuthEventNames.AUTH_HTTP_ERROR, ({ data }) => {
    toast.error('Error', { description: data.message });
  });

  globalEventBus.on(AuthEventNames.AUTH_LOGOUT, async ({ data }) => {
    if (logoutInProgress) {
      logger.warn('AUTH_LOGOUT already in progress, ignoring', { data });
      return;
    }

    logoutInProgress = true;
    console.info(`[Auth] Logout event received. Reason: ${data.reason}`);

    try {
      await clearUserSession(
        data.reason === 'manual' ? 'user_logout' : 'auth_expired_logout'
      );
    } catch (error) {
      toast.error('Error during logout', {
        description: (error as Error)?.message,
      });
    } finally {
      logoutInProgress = false;
    }

    if (isInAuthGatedRoute()) {
      navigate({ to: '/login', replace: true });
    }
  });

  globalEventBus.on(AuthEventNames.AUTH_DATA_SET, () => {
    void userStore.refresh();
  });
}

export async function clearUserSession(
  reason: 'user_logout' | 'auth_expired_logout' = 'auth_expired_logout'
): Promise<void> {
  if (inElectron()) {
    await electronApis.context.stopContextCollection();
    electronApis.featureFlags.updateFeatureFlags('{}');
  }

  await cleanupQueries();
  // userStore.clear() also empties the meeting ledgers: meetingStore
  // reacts to every userId change and rehydrates them for the (now
  // absent) user, so the next session cannot retry this user's uploads
  userStore.clear();
  posthog.reset();
  clearSingularUser();
  clearLocalStorage();
  agentsStore.reset();
  routinesStore.clearAllCreateFlowState();
  await littlebirdSdk.logout(reason);
  resetRoutineReadState();
  resetMeetingCircles();
  clearWebOnboardingSessionCompletion();
  clearWebOnboardingCoachMarkStorage();
}
